import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Edit2, Trash2, Phone, MapPin, CheckCircle, XCircle } from 'lucide-react';
import { Guest, CreateGuestRequest } from '../types';
import { guestAPI } from '../services/api';

const GuestManagement: React.FC = () => {
  const navigate = useNavigate();
  const [guests, setGuests] = useState<Guest[]>([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editingGuest, setEditingGuest] = useState<Guest | null>(null);
  const [formData, setFormData] = useState<CreateGuestRequest>({
    name: '',
    whatsapp_number: '',
    room_number: '',
    status: 'checked_in', // default to checked in
    marketing_opt_in: true,
  });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'checked_in' | 'checked_out'>('all');
  const [feedbackFilter, setFeedbackFilter] = useState<'all' | 'with' | 'without'>('all');
  const [page, setPage] = useState(1);
  const pageSize = 10;

  useEffect(() => {
    fetchGuests();
  }, []);

  const fetchGuests = async () => {
    try {
      const data = await guestAPI.getAllGuests();
      setGuests(data);
    } catch (error) {
      console.error('Error fetching guests:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingGuest) {
        await guestAPI.updateGuest(editingGuest.id, {
          name: formData.name,
          room_number: formData.room_number,
          whatsapp_number: formData.whatsapp_number,
          marketing_opt_in: formData.marketing_opt_in,
        });
      } else {
        await guestAPI.createGuest(formData);
      }
      await fetchGuests();
      resetForm();
    } catch (error) {
      console.error('Error saving guest:', error);
    }
  };

  const handleStatusChange = async (id: string, status: 'checked_in' | 'checked_out') => {
    try {
      await guestAPI.updateGuestStatus(id, status);
      await fetchGuests();
    } catch (error) {
      console.error('Error updating guest status:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this guest?')) {
      try {
        await guestAPI.deleteGuest(id);
        await fetchGuests();
      } catch (error) {
        console.error('Error deleting guest:', error);
      }
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      whatsapp_number: '',
      room_number: '',
      status: 'checked_out',
      marketing_opt_in: true,
    });
    setEditingGuest(null);
    setShowModal(false);
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="text-lg text-grey-guestby">Loading guests...</div>
        </div>
      </div>
    );
  }

  const filteredGuests = guests.filter((g) => {
    const matchesSearch =
      g.name.toLowerCase().includes(search.toLowerCase()) ||
      g.room_number.toLowerCase().includes(search.toLowerCase());
    const matchesStatus =
      statusFilter === 'all' ? true : g.status === statusFilter;
    const hasFeedback = (g.feedback_count || 0) > 0;
    const matchesFeedback =
      feedbackFilter === 'all'
        ? true
        : feedbackFilter === 'with'
        ? hasFeedback
        : !hasFeedback;
    return matchesSearch && matchesStatus && matchesFeedback;
  });

  const totalPages = Math.max(1, Math.ceil(filteredGuests.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const paginated = filteredGuests.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize
  );

  return (
    <div className="p-6 flex flex-col gap-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold text-black-guestby">Guest Management</h1>
        <button
          onClick={() => setShowModal(true)}
          className="flex items-center space-x-2 bg-blue-guestby text-black-guestby px-4 py-2 rounded-lg hover:bg-blue-400 transition-colors"
        >
          <Plus className="h-5 w-5" />
          <span>Add Guest</span>
        </button>
      </div>

      {/* Guests List */}
      <div className="bg-white rounded-lg shadow-md border border-gray-200">
        <div className="p-6 border-b border-gray-200 flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <h2 className="text-xl font-semibold text-black-guestby">All Guests</h2>
          <div className="flex flex-col md:flex-row md:items-center gap-3">
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search by name or room"
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-guestby"
            />
            <select
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value as any);
                setPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-guestby"
            >
              <option value="all">All statuses</option>
              <option value="checked_in">Checked In</option>
              <option value="checked_out">Checked Out</option>
            </select>
            <select
              value={feedbackFilter}
              onChange={(e) => {
                setFeedbackFilter(e.target.value as any);
                setPage(1);
              }}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-guestby"
            >
              <option value="all">All feedback</option>
              <option value="with">With feedback</option>
              <option value="without">No feedback</option>
            </select>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-guestby uppercase tracking-wider">
                  Guest
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-guestby uppercase tracking-wider">
                  Contact
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-guestby uppercase tracking-wider">
                  Room
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-guestby uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-guestby uppercase tracking-wider">
                  Feedback
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-guestby uppercase tracking-wider">
                  Chat
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-grey-guestby uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {paginated.map((guest) => (
                <tr key={guest.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-black-guestby">{guest.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <Phone className="h-4 w-4 text-grey-guestby" />
                      <span className="text-sm text-black-guestby">{guest.whatsapp_number}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      <MapPin className="h-4 w-4 text-grey-guestby" />
                      <span className="text-sm text-black-guestby">{guest.room_number}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center space-x-2">
                      {guest.status === 'checked_in' ? (
                        <>
                          <CheckCircle className="h-4 w-4 text-green-500" />
                          <span className="text-sm text-green-600">Checked In</span>
                        </>
                      ) : (
                        <>
                          <XCircle className="h-4 w-4 text-grey-guestby" />
                          <span className="text-sm text-grey-guestby">Checked Out</span>
                        </>
                      )}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <span
                      onClick={() => navigate(`/feedbacks?guestId=${guest.id}`)}
                      className={`inline-flex px-2 py-1 rounded-full text-xs font-semibold cursor-pointer ${
                        (guest.feedback_count || 0) > 0
                          ? 'bg-green-100 text-green-800'
                          : 'bg-amber-100 text-amber-800'
                      }`}
                    >
                      {(guest.feedback_count || 0) > 0 ? 'Feedback' : 'Pending'}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm">
                    <button
                      onClick={() => navigate(`/chats?guestId=${guest.id}`)}
                      className="text-blue-600 hover:text-blue-900 text-xs font-medium"
                    >
                      Open Chat
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                    <button
                      onClick={() => handleStatusChange(
                        guest.id,
                        guest.status === 'checked_in' ? 'checked_out' : 'checked_in'
                      )}
                      className={`px-3 py-1 rounded text-xs font-medium ${
                        guest.status === 'checked_in'
                          ? 'bg-red-100 text-red-800 hover:bg-red-200'
                          : 'bg-green-100 text-green-800 hover:bg-green-200'
                      }`}
                    >
                      {guest.status === 'checked_in' ? 'Check Out' : 'Check In'}
                    </button>
                    <button
                      onClick={() => {
                        setEditingGuest(guest);
                        setFormData({
                          name: guest.name,
                          whatsapp_number: guest.whatsapp_number,
                          room_number: guest.room_number,
                          status: guest.status,
                          marketing_opt_in: guest.marketing_opt_in !== false,
                        });
                        setShowModal(true);
                      }}
                      className="text-blue-600 hover:text-blue-900"
                    >
                      <Edit2 className="h-4 w-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(guest.id)}
                      className="text-red-600 hover:text-red-900"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="p-4 flex items-center justify-between">
          <div className="text-sm text-grey-guestby">
            Page {currentPage} of {totalPages}
          </div>
          <div className="space-x-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-3 py-1 text-sm border rounded disabled:opacity-50"
            >
              Prev
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-1 text-sm border rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Add/Edit Guest Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[9999]">
          <div className="bg-white rounded-lg p-6 w-full max-w-md shadow-xl">
            <h2 className="text-xl font-semibold text-black-guestby mb-4">
              {editingGuest ? 'Edit Guest' : 'Add New Guest'}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-grey-guestby mb-1">
                  Guest Name
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-guestby"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-grey-guestby mb-1">
                  WhatsApp Number
                </label>
                <input
                  type="tel"
                  value={formData.whatsapp_number}
                  onChange={(e) => setFormData({ ...formData, whatsapp_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-guestby"
                  placeholder="+1234567890"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-grey-guestby mb-1">
                  Room Number
                </label>
                <input
                  type="text"
                  value={formData.room_number}
                  onChange={(e) => setFormData({ ...formData, room_number: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-guestby"
                  placeholder="101"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-grey-guestby mb-1">
                  Status
                </label>
                <select
                  value={formData.status}
                  onChange={(e) => setFormData({ ...formData, status: e.target.value as 'checked_in' | 'checked_out' })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-guestby"
                >
                  <option value="checked_in">Checked In</option>
                  <option value="checked_out">Checked Out</option>
                </select>
              </div>
              <label className="flex items-center gap-2 text-sm text-black-guestby cursor-pointer">
                <input
                  type="checkbox"
                  checked={formData.marketing_opt_in !== false}
                  onChange={(e) =>
                    setFormData({ ...formData, marketing_opt_in: e.target.checked })
                  }
                  className="rounded border-gray-300"
                />
                Receive marketing messages on WhatsApp (offers, campaigns)
              </label>
              <div className="flex space-x-3 pt-4">
                <button
                  type="submit"
                  className="flex-1 bg-blue-guestby text-black-guestby py-2 px-4 rounded-md hover:bg-blue-400 transition-colors"
                >
                  {editingGuest ? 'Update' : 'Add'} Guest
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 bg-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-400 transition-colors"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default GuestManagement;
